<template>
  <div v-show="visible" class="comments">
    <div class="comments_header">
      <div class="comments_header_top">
        <button class="comments_back" @click="closeComents">
          <img :src="currentImage.hide" />
        </button>
        <h2 class="comments_header_title">{{ $t("comments.title") }}</h2>
      </div>

      <div class="type_comments">
        <button
          class="type_comments_btn"
          :class="{ active: activeFilter === 'new' }"
          @click="activeFilter = 'new'"
        >
          {{ $t("comments.filter.new") }}
        </button>

        <button
          class="type_comments_btn"
          :class="{ active: activeFilter === 'author' }"
          @click="activeFilter = 'author'"
        >
          {{ $t("comments.filter.author") }}
        </button>

        <button
          class="type_comments_btn"
          :class="{ active: activeFilter === 'my' }"
          @click="activeFilter = 'my'"
        >
          {{ $t("comments.filter.my") }}
        </button>
      </div>
    </div>

    <div class="comments_sroll">
      <Loader v-if="commentLoad" class="comments_loader" />
      <Comment
        v-else
        v-for="comment in filteredComments"
        :key="comment.id"
        :comment="comment"
        @setCommentText="commentText = $event"
        @deleted="removeComment"
      />

      <div
        class="comments_list_empty"
        v-if="filteredComments.length === 0 && !commentLoad"
      >
        {{ $t("comments.notFound") }}
      </div>
    </div>

    <form v-if="auth.user" class="comments_send" @submit.prevent="sendComment">
      <Avatar
        :user="this.auth.user"
        class="user_avatar"
        @click.stop="openProfile(auth.user?.id)"
      />
      <input
        type="text"
        class="comment_input"
        @keyup.enter="sendComment"
        :class="{ error: commentError }"
        :placeholder="$t('comments.input')"
        v-model="commentText"
        name="comment_input"
        id="comment_input"
        :disabled="commentSendLoad"
      />

      <span v-if="commentError" class="comment_error">
        <button class="comment_error_close" @click="commentError = ''">
          <img :src="currentImage.close" />
        </button>
        {{ commentError }}
      </span>

      <Loader v-if="commentSendLoad" class="comments_send_loader" />
      <button v-else class="comments_send_btn" type="submit">
        <img :src="currentImage.send" />
      </button>
    </form>
  </div>
</template>

<script>
import Comment from "./Comment.vue";
import Avatar from "@/components/Avatar.vue";
import { getComments, addComment } from "@/api/axios.js";
import { useAuthStore } from "@/stores/authStore.js";
import { useModalStore } from "@/stores/modalStore";
import Loader from "@/UI/Loaders/Loader.vue";
import { useThemeStore } from "@/stores/themeStore";
import { useOpenProfile } from "../../composables/useOpenProfile";

export default {
  name: "Comments",
  emits: ["commentVisible", "commentsChanged"],
  components: { Comment, Avatar, Loader },
  props: {
    visible: {
      type: Boolean,
      required: false,
      default: false,
    },
    post: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      commentsList: [],
      auth: useAuthStore(),
      modal: useModalStore(),
      commentText: "",
      commentError: "",
      commentSendLoad: false,
      commentLoad: false,
      activeFilter: "new",
      currentImage: {},
      themeStore: useThemeStore(),
      openProfile: useOpenProfile().openProfile,
    };
  },
  methods: {
    closeComents() {
      this.$emit("commentVisible", false);
    },
    updateImage() {
      this.currentImage.hide = this.themeStore.isDark
        ? new URL("/arrow-left.svg", import.meta.url).href
        : new URL("/gray-arrow.svg", import.meta.url).href;

      this.currentImage.close = this.themeStore.isDark
        ? new URL("/close.svg", import.meta.url).href
        : new URL("/close-light.svg", import.meta.url).href;

      this.currentImage.send = this.themeStore.isDark
        ? new URL("/send.svg", import.meta.url).href
        : new URL("/send-dark.svg", import.meta.url).href;
    },
    async loadComments() {
      this.commentLoad = true;
      try {
        if (this.auth.token) {
          const res = await getComments(this.post?.id);
          this.commentsList = res.data.reverse();
        }
      } catch (e) {
        console.log("Comments error: ", e);
      } finally {
        this.commentLoad = false;
      }
    },
    async sendComment() {
      if (this.commentSendLoad) {
        return;
      }

      if (this.commentText.length == 0) {
        this.commentError = this.$t("comments.empty");
        return;
      }

      if (this.commentText.length >= 1024) {
        this.commentError = this.$t("comments.longError");
        return;
      }

      try {
        this.commentSendLoad = true;
        const res = await addComment(this.post.id, this.commentText);
        const newComment = res.data;
        this.commentsList.unshift(newComment);
        this.$emit("commentsChanged", +1);
        this.commentText = "";
        this.commentError = "";
      } catch (e) {
        this.commentError = e.response?.data?.message;
        console.log("Send comment error: ", e);
      } finally {
        this.commentSendLoad = false;
      }
    },

    removeComment(id) {
      this.commentsList = this.commentsList.filter((c) => c.id !== id);
      this.$emit("commentsChanged", -1);
    },
  },
  computed: {
    filteredComments() {
      if (!this.commentsList.length) return [];

      switch (this.activeFilter) {
        case "author":
          if (!this.post?.user?.id) return [];
          return this.commentsList.filter(
            (c) => c.author?.id === this.post.user.id,
          );
        case "my":
          if (!this.auth.user?.id) return [];
          return this.commentsList.filter(
            (c) => c.author?.id === this.auth.user.id,
          );
        case "new":
        default:
          return this.commentsList;
      }
    },
  },
  watch: {
    "themeStore.isDark": function () {
      this.updateImage();
    },
  },
  mounted() {
    this.loadComments();
    this.updateImage();
  },
};
</script>

<style>
.comments {
  border-radius: 31px;
  width: 100%;
  aspect-ratio: 9 / 15;
  box-shadow: 0px 0px 15px 2px rgba(0, 0, 0, 0.1);
  background-color: var(--comments-bg);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  color: var(--text-color);
}

.comments_header {
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.comments_header_top {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.comments_header_title {
  font-size: 20px;
  align-self: center;
}

.comments_back {
  background: transparent;
  cursor: pointer;
}

.comments_back:hover {
  opacity: 60%;
}

.type_comments {
  display: flex;
  gap: 8px;
}

.type_comments_btn {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  color: rgba(114, 114, 114, 1);
  padding: 6px 12px;
  cursor: pointer;
  font-weight: 700;
  transition: background 0.2s ease;
}

.type_comments_btn.active {
  background: var(--comment-type-btn);
  color: rgba(28, 28, 30, 1);
}

.type_comments_btn.active:hover {
  background: var(--comment-type-btn);
}

.type_comments_btn:hover {
  background: var(--hover);
}

.comments_loader {
  margin: auto;
  opacity: 50%;
}

.comments_send_loader {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
}

.comments_send_loader div {
  width: 22px !important;
  height: 22px !important;
  border-width: 4px;
}

.comments_list_empty {
  margin: auto;
  padding: 10px 15px;
  background: rgba(255, 255, 255, 0.1);
  font-size: 13px;
  color: var(--text-color);
  opacity: 50%;
  border-radius: 12px;
  position: relative;
}

.comments_sroll {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
}

.comments_send {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px 12px;
  gap: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
}

.comments_send_btn {
  background: transparent;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
}

.comments_send_btn img:hover {
  opacity: 60%;
  cursor: pointer;
}

.comments_send_btn.error {
  border: 1px solid rgb(197, 41, 41);
}

.comment_input {
  padding: 0px 16px;
  height: 32px;
  border-radius: 16px;
  background: var(--input-bg-color);
  width: 100%;
  font-size: 11px;
  color: var(--text-color);
}

.comment_input:disabled {
  opacity: 40%;
}

.comment_error {
  color: rgb(197, 41, 41);
  background: var(--color-picker-border);
  box-shadow: 0px 0px 7px 0px rgba(0, 0, 0, 0.25);
  font-size: 12px;
  padding-bottom: 5px;
  position: absolute;
  bottom: 65px;
  padding: 5px 15px;
  border-radius: 20px;
  font-weight: bold;
}

.comment_error_close {
  background: var(--input-bg-color);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  position: absolute;
  top: -5px;
  right: -5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.comment_error_close:hover {
  background: var(--hover);
}

.comment_error_close img {
  width: 8px;
  height: 8px;
}

@media (max-width: 1100px) {
  .comments_back {
    rotate: 90deg;
  }

  .comments_sroll {
    min-height: 200px;
  }
}
</style>
